/**
 * 在浏览器里直接驱动 `@poker/shared` 引擎（§11.7 / §11.8）。
 *
 * 这一层的全部职责就三件事：
 *  1. **持有私密状态**。`HandState` 含 `deck` / `deckIndex` / 所有人的底牌，它只存在于本文件的闭包里，
 *     对外的唯一出口是 `redactHandState(state, perspectiveSeat)`。上层（store / 组件）永远拿不到它。
 *  2. **把引擎事件排成有节奏的帧**。引擎的 `applyAction` 会一路推进到下一个决策点，
 *     一次可能产出「跟注 → 收池 → 发翻牌 → 轮转」一整串事件；照单全发的话，跑马时
 *     五张公共牌会在同一帧里砸出来，没法慢放。所以按 `pacingHintMs` 切帧。
 *  3. **管理观察视角**。传手机模式下「谁在看」会变，而底牌脱敏是按视角做的，
 *     所以视角切换必须发生在**发帧之前**，否则上一位玩家的底牌会跟着新帧一起送出去。
 *
 * 时钟：本地模式 `displayDeadlineAt` / `hardDeadlineAt` 恒为 null（§11.8）。
 * 手机还在传递途中倒计时就走完了，引擎会按 `defaultAutoIntent` 自动弃牌 —— 那是纯粹的 bug 体验。
 */
import {
  ROOM_DEFAULTS,
  SEAT_LIMITS,
  applyAction,
  redactHandState,
  shuffleDeck,
  startHand,
  ulid,
} from '@poker/shared'
import type {
  ActionIntent,
  AnteMode,
  CardId,
  Chips,
  DeckVariant,
  HandEvent,
  HandId,
  HandState,
  LegalActions,
  Millis,
  PublicHandPlayer,
  PublicHandState,
  RoomConfig,
  Rng,
  SeatIndex,
  Street,
} from '@poker/shared'

import { createWebCryptoRng } from '../engineLocal/rng.js'
import {
  applyFinalStacks,
  canDealNextHand,
  nameOfSeat,
  toSeatSnapshots,
  type LocalPlayer,
} from '../local/localSeats.js'

import {
  PROTOCOL_VERSION,
  createRealScheduler,
  type ActionKind,
  type ClientMessage,
  type HoleCardsView,
  type RoomPhaseLite,
  type Scheduler,
  type SeatView,
  type ServerMessage,
  type TimerHandle,
  type Transport,
} from './types.js'

// ============================================================================
// 节奏常量
// ============================================================================

/** 河牌发完到亮牌之间的停顿。没有这个停顿，摊牌和最后一张公共牌会在同一帧出现。 */
export const SHOWDOWN_BEAT_MS = 700
/** 两手之间的默认间隔。比服务端的 `ROOM_LIFECYCLE.INTER_HAND_DELAY_MS` 长一点：
 *  本地模式要留时间给大家看结果、再把手机递出去。 */
export const LOCAL_INTER_HAND_DELAY_MS = 4000

// ============================================================================
// 对外形状
// ============================================================================

export interface LocalSetup {
  readonly players: readonly LocalPlayer[]
  readonly smallBlind: Chips
  readonly bigBlind: Chips
  readonly ante?: Chips
  readonly anteMode?: AnteMode
  /** 只用于展示与重开；每位玩家的实际筹码在 `players[*].stack` 上 */
  readonly startingStack: Chips
  readonly roomName?: string
  /** 玩法；缺省标准。大厅入口目前只开标准局，参数先接通（测试与 harness 用） */
  readonly deckVariant?: DeckVariant
  /** 手牌结束后自动开下一手；false 时需要显式 `{type:'nextHand'}` */
  readonly autoNextHand?: boolean
  readonly interHandDelayMs?: number
  /** 仅供测试注入。缺省用 `crypto.getRandomValues` + 拒绝采样（红线 #15） */
  readonly rng?: Rng
  /** 仅供测试注入。缺省用真 `setTimeout` */
  readonly scheduler?: Scheduler
  readonly now?: () => Millis
}

export interface LocalEngineTransport extends Transport {
  readonly mode: 'local'
  /** 当前观察视角。UI 不该依赖它做业务判断（那是 `view.viewerSeat` 的活），调试用。 */
  perspectiveSeat(): SeatIndex | null
}

// ============================================================================
// 内部工具
// ============================================================================

function toIntent(action: ActionKind, to: Chips | undefined): ActionIntent | null {
  switch (action) {
    case 'fold':
      return { type: 'fold' }
    case 'check':
      return { type: 'check' }
    case 'call':
      return { type: 'call' }
    case 'allIn':
      return { type: 'allIn' }
    case 'bet':
    case 'raise':
      if (to === undefined || !Number.isInteger(to)) return null
      return { type: action, to }
    default:
      return null
  }
}

/** 公共牌张数 → 街。中间帧靠它把「未来的街」挡回去。 */
function streetForBoardCount(n: number): Street {
  if (n >= 5) return 'river'
  if (n === 4) return 'turn'
  if (n >= 3) return 'flop'
  return 'preflop'
}

/**
 * 中间帧的视图。
 *
 * 跑马时引擎一次性把 flop/turn/river 全发完，但 UI 必须一街一街地放；摊牌前也要留一拍。
 * 这里**不重建中间态引擎状态** —— 那等于把引擎逻辑抄第二遍，必然漂移。
 * 做法是对**最终的脱敏视图**只做减法：
 *   · `board` 截到这一帧为止已经发出的张数；
 *   · 结算字段（result / showdown）清空，`actionOn` 置 null（中间帧不该出现行动权）；
 *   · 底牌只保留「观察者自己的」和「这一帧之前已经 `cardsRevealed` 过的」。
 * 减法永远安全：不可能因此多暴露任何信息。最后一帧不走这里，直接用引擎给的完整视图。
 */
function progressiveView(
  final: PublicHandState,
  boardCount: number,
  revealed: ReadonlySet<SeatIndex>,
): PublicHandState {
  const viewer = final.viewerSeat
  const players: Record<SeatIndex, PublicHandPlayer> = {}
  for (const p of Object.values(final.players)) {
    const mayShow = (viewer !== null && p.seat === viewer) || revealed.has(p.seat)
    players[p.seat] = {
      seat: p.seat,
      accountId: p.accountId,
      startingStack: p.startingStack,
      stack: p.stack,
      holeCards: mayShow ? p.holeCards : null,
      hasCards: p.hasCards,
      status: p.status,
      committedThisRound: p.committedThisRound,
      committedThisHand: p.committedThisHand,
      hasActedThisRound: p.hasActedThisRound,
      lastAction: p.lastAction,
      hasShownCards: p.hasShownCards,
    }
  }
  return {
    schemaVersion: final.schemaVersion,
    handId: final.handId,
    handNo: final.handNo,
    config: final.config,
    viewerSeat: viewer,
    buttonSeat: final.buttonSeat,
    smallBlindSeat: final.smallBlindSeat,
    bigBlindSeat: final.bigBlindSeat,
    isHeadsUp: final.isHeadsUp,
    deadButton: final.deadButton,
    dealtSeats: final.dealtSeats,
    players,
    board: final.board.slice(0, boardCount),
    phase: streetForBoardCount(boardCount),
    street: streetForBoardCount(boardCount),
    betToMatch: final.betToMatch,
    lastFullRaiseSize: final.lastFullRaiseSize,
    lastAggressorSeatThisStreet: final.lastAggressorSeatThisStreet,
    actionOn: null,
    potTotal: final.potTotal,
    pots: final.pots,
    actionLog: final.actionLog,
    uncalledReturn: final.uncalledReturn,
    showdown: [],
    result: null,
  }
}

/** 一帧：一次 `emit` 里发出去的所有消息（§5.7 的批量合并）。 */
interface FramePlan {
  readonly delayMs: number
  readonly events: HandEvent[]
  /** 这一帧结束时公共牌的张数 */
  boardCount: number
  /** 这一帧结束时已对全场亮牌的座位 */
  revealed: Set<SeatIndex>
  /** 这一帧要切到的视角（null 表示不切） */
  perspective: { readonly seat: SeatIndex | null; readonly reason: 'turn' | 'handEnd' } | null
  legal: LegalActions | null
  isLast: boolean
}

/** 事件流 → 帧。切帧点只有两处：发公共牌之前、进入摊牌/结算之前。 */
function planFrames(events: readonly HandEvent[], boardBefore: number, revealedBefore: ReadonlySet<SeatIndex>): FramePlan[] {
  const frames: FramePlan[] = []
  let boardCount = boardBefore
  const revealed = new Set<SeatIndex>(revealedBefore)
  // 亮牌与结算各切一次帧，各有各的停顿：
  // 跑马时引擎会先后产出两批 cardsRevealed（翻牌后一批、runShowdown 再一批），
  // 用一个标志会让第二批和河牌挤在同一帧，用两个标志则刚好是「河牌 → 结算」一拍。
  let revealSplitDone = false
  let settleSplitDone = false

  const newFrame = (delayMs: number): FramePlan => {
    const f: FramePlan = {
      delayMs,
      events: [],
      boardCount,
      revealed: new Set(revealed),
      perspective: null,
      legal: null,
      isLast: false,
    }
    frames.push(f)
    return f
  }

  let cur = newFrame(0)

  for (const ev of events) {
    if (ev.t === 'streetDealt' && cur.events.length > 0) {
      cur = newFrame(ev.pacingHintMs)
    } else if (!revealSplitDone && (ev.t === 'cardsRevealed' || ev.t === 'cardsMucked') && cur.events.length > 0) {
      revealSplitDone = true
      cur = newFrame(SHOWDOWN_BEAT_MS)
    } else if (!settleSplitDone && (ev.t === 'potAwarded' || ev.t === 'handComplete') && cur.events.length > 0) {
      settleSplitDone = true
      cur = newFrame(SHOWDOWN_BEAT_MS)
    }

    cur.events.push(ev)

    switch (ev.t) {
      case 'streetDealt':
        boardCount += ev.cards.length
        break
      case 'cardsRevealed':
        revealed.add(ev.seat)
        break
      case 'actionOn':
        // 行动权换人 = 手机要换人。视角必须在这一帧发出**之前**切过去。
        cur.perspective = { seat: ev.seat, reason: 'turn' }
        cur.legal = ev.legal
        break
      case 'handComplete':
        // 摊牌后切成旁观视角（seat = null）：否则最后一个行动者的底牌会留在快照里，
        // 而这时几个人正凑在同一块屏幕前看结果 —— 盖牌的人等于当众亮牌。
        cur.perspective = { seat: null, reason: 'handEnd' }
        break
      default:
        break
    }
    cur.boardCount = boardCount
    cur.revealed = new Set(revealed)
  }

  const last = frames[frames.length - 1]
  if (last !== undefined) last.isLast = true
  return frames.filter((f) => f.events.length > 0)
}

// ============================================================================
// 工厂
// ============================================================================

export function createLocalEngineTransport(setup: LocalSetup): LocalEngineTransport {
  const sched: Scheduler = setup.scheduler ?? createRealScheduler()
  const now = setup.now ?? (() => Date.now())
  const rng: Rng = setup.rng ?? createWebCryptoRng()
  const autoNextHand = setup.autoNextHand ?? true
  const interHandDelayMs = setup.interHandDelayMs ?? LOCAL_INTER_HAND_DELAY_MS

  const listeners = new Set<(m: ServerMessage) => void>()

  // ——— 私密状态。以下三行是整个应用里唯一持有「所有人底牌」的地方 ———
  let state: HandState | null = null
  let players: readonly LocalPlayer[] = setup.players.slice()
  let prevBigBlindSeat: SeatIndex | null = null
  /** 上一手 complete 的小盲位置（死按钮靠它定按钮）；与 prevBigBlindSeat 同时推进 */
  let prevSmallBlindSeat: SeatIndex | null = null

  let handNo = 0
  let phase: RoomPhaseLite = 'lobby'
  let perspective: SeatIndex | null = null
  let pendingLegal: LegalActions | null = null
  let closed = false

  const queue: { readonly delayMs: number; readonly run: () => void }[] = []
  let timer: TimerHandle | null = null
  let pumping = false
  let nextHandTimer: TimerHandle | null = null

  const roomConfig: RoomConfig = {
    ...ROOM_DEFAULTS,
    name: setup.roomName ?? '本地牌局',
    maxSeats: players.length,
    smallBlind: setup.smallBlind,
    bigBlind: setup.bigBlind,
    anteMode: setup.anteMode ?? 'none',
    ante: setup.ante ?? 0,
    startingStack: setup.startingStack,
    deckVariant: setup.deckVariant === 'shortDeck' ? 'shortDeck' : 'standard',
    // 本地模式没有倒计时（§11.8）。留 0 让任何误读这个配置的代码也拿不到一个「像样」的超时值。
    actionTimeoutMs: 0,
    timeBankMs: 0,
    interHandDelayMs,
    autoStart: autoNextHand,
    botsAllowed: false,
    maxBots: 0,
    // 本地模式所有人当面看结果，摊牌一律全亮，省掉「谁先亮」的规则解释
    showdownPolicy: 'revealAll',
    newPlayerPolicy: 'dealInImmediately',
  }

  function emit(m: ServerMessage): void {
    if (closed) return
    // 复制一份订阅者列表：回调里退订/新订阅都不该影响本次派发
    for (const cb of Array.from(listeners)) cb(m)
  }

  function emitError(code: string, message: string, refClientActionId?: string): void {
    emit({ type: 'error', code, message, retryable: false, refClientActionId })
  }

  function seatViews(): SeatView[] {
    return players.map((p) => ({
      seat: p.seat,
      name: p.name,
      stack: p.stack,
      sittingOut: p.sittingOut,
      isBot: false,
      // 本地模式没有网络，所有人恒为「在线」
      connected: true,
    }))
  }

  function emitRoomSnapshot(): void {
    emit({
      type: 'roomSnapshot',
      roomCode: null,
      config: roomConfig,
      seats: seatViews(),
      phase,
      handNo,
    })
  }

  /** 唯一的牌面出口。任何时候要把手牌状态送出去都必须经过这里。 */
  function emitHandSnapshot(view?: PublicHandState, legal?: LegalActions | null): void {
    if (state === null) return
    const v = view ?? redactHandState(state, perspective)
    const l = legal === undefined ? (v.actionOn !== null && v.actionOn === perspective ? pendingLegal : null) : legal
    emit({ type: 'handSnapshot', handId: v.handId, view: v, legal: l })
  }

  function setPerspective(seat: SeatIndex | null, reason: 'turn' | 'handEnd' | 'manual'): void {
    perspective = seat
    emit({ type: 'perspectiveChanged', seat, name: seat === null ? null : nameOfSeat(players, seat), reason })
  }

  // —— 帧队列 ——

  function pump(): void {
    if (closed || pumping || timer !== null) return
    pumping = true
    try {
      while (queue.length > 0 && !closed) {
        const f = queue[0]
        if (f === undefined) break
        if (f.delayMs > 0) {
          timer = sched.schedule(onTimer, f.delayMs)
          return
        }
        queue.shift()
        f.run()
      }
    } finally {
      pumping = false
    }
    if (!closed && queue.length === 0) afterDrain()
  }

  function onTimer(): void {
    timer = null
    const f = queue.shift()
    if (f !== undefined) f.run()
    pump()
  }

  function afterDrain(): void {
    if (state === null || state.result === null) return
    if (phase === 'ended' || !autoNextHand || nextHandTimer !== null) return
    nextHandTimer = sched.schedule(() => {
      nextHandTimer = null
      startNextHand()
    }, interHandDelayMs)
  }

  /** 把一批引擎事件排进队列。`stateAfter` 是这一批跑完后的权威状态。 */
  function enqueueBatch(events: readonly HandEvent[], stateBefore: HandState | null, stateAfter: HandState): void {
    const boardBefore = stateBefore?.board.length ?? 0
    const revealedBefore = new Set<SeatIndex>()
    if (stateBefore !== null) {
      for (const p of Object.values(stateBefore.players)) {
        if (p.hasShownCards) revealedBefore.add(p.seat)
      }
    }

    for (const plan of planFrames(events, boardBefore, revealedBefore)) {
      queue.push({
        delayMs: plan.delayMs,
        run: () => {
          // ① 先切视角，再发消息。顺序反了就会把上一位玩家的底牌一起发出去。
          if (plan.perspective !== null) {
            pendingLegal = plan.legal
            setPerspective(plan.perspective.seat, plan.perspective.reason)
          }
          // ② 增量消息（动画脚本）
          for (const m of messagesFor(plan, stateAfter)) emit(m)
          // ③ 权威视图收尾，tableStore 只认这一条
          const full = redactHandState(stateAfter, perspective)
          if (plan.isLast) {
            emitHandSnapshot(full, full.actionOn !== null && full.actionOn === perspective ? plan.legal : null)
          } else {
            emitHandSnapshot(progressiveView(full, plan.boardCount, plan.revealed), null)
          }
          // ④ 手牌结束的房间级收尾
          if (plan.events.some((e) => e.t === 'handComplete')) settleHand(stateAfter)
        },
      })
    }
    pump()
  }

  /** 引擎事件 → 协议消息（§5.6 映射表）。连续的 `blindPosted` 合并成一条。 */
  function messagesFor(plan: FramePlan, stateAfter: HandState): ServerMessage[] {
    const handId = stateAfter.config.handId as HandId
    const out: ServerMessage[] = []
    const posts: { seat: SeatIndex; kind: 'sb' | 'bb' | 'ante' | 'dead'; amount: Chips; allIn: boolean }[] = []
    const flushPosts = (): void => {
      if (posts.length === 0) return
      out.push({ type: 'blindsPosted', handId, posts: posts.slice() })
      posts.length = 0
    }

    for (const ev of plan.events) {
      if (ev.t !== 'blindPosted') flushPosts()
      switch (ev.t) {
        case 'handStarted':
          out.push({
            type: 'handStarted',
            handId,
            handNo: ev.handNo,
            buttonSeat: ev.buttonSeat,
            sbSeat: ev.sbSeat,
            bbSeat: ev.bbSeat,
            deadButton: ev.deadButton,
            // ★ accountId 不下发：本地席位与账号无映射（红线 #11），传出去只会被误当成身份
            seats: ev.players.map((p) => ({ seat: p.seat, stack: p.stack, position: p.position })),
          })
          break
        case 'blindPosted':
          posts.push({ seat: ev.seat, kind: ev.kind, amount: ev.amount, allIn: ev.allIn })
          break
        case 'holeCardsDealt': {
          // 只有当前视角的那两张是 'shown'，其余一律 'hidden'（判别联合，不允许「有字段填 null」）
          const seats = Object.entries(ev.cardsBySeat).map(([k, cards]) => {
            const seat = Number(k)
            const view: HoleCardsView =
              perspective !== null && seat === perspective
                ? { visibility: 'shown', cards: [cards[0], cards[1]] as readonly [CardId, CardId] }
                : { visibility: 'hidden', count: 2 }
            return { seat, holeCards: view }
          })
          out.push({ type: 'holeCardsDealt', handId, seats })
          break
        }
        case 'actionOn':
          out.push({
            type: 'turnChanged',
            handId,
            actionNo: stateAfter.actionLog.length,
            seat: ev.seat,
            // 红线：本地模式没有倒计时
            displayDeadlineAt: null,
            hardDeadlineAt: null,
          })
          out.push({ type: 'actionPrompt', handId, actionNo: stateAfter.actionLog.length, legal: ev.legal })
          break
        case 'playerActed':
          out.push({
            type: 'playerActed',
            handId,
            action: ev.action,
            stackAfter: stateAfter.players[ev.action.seat]?.stack ?? 0,
          })
          break
        case 'streetDealt':
          out.push({
            type: 'communityCards',
            handId,
            street: ev.street,
            cards: ev.cards.slice(),
            pacingHintMs: ev.pacingHintMs,
          })
          break
        case 'uncalledReturned':
          out.push({
            type: 'uncalledBetReturned',
            handId,
            seat: ev.seat,
            amount: ev.amount,
            stackAfter: stateAfter.players[ev.seat]?.stack ?? 0,
          })
          break
        case 'potsUpdated':
          out.push({ type: 'potsUpdated', handId, pots: ev.pots.slice(), potTotal: ev.potTotal })
          break
        case 'runItOut':
          out.push({ type: 'runItOut', handId, remainingStreets: ev.remainingStreets.slice() })
          break
        case 'cardsRevealed':
          out.push({
            type: 'cardsRevealed',
            handId,
            seat: ev.seat,
            cards: [ev.cards[0], ev.cards[1]],
            rank: ev.rank,
            voluntary: ev.voluntary,
          })
          break
        case 'cardsMucked':
          out.push({ type: 'cardsMucked', handId, seat: ev.seat })
          break
        case 'potAwarded':
          // 不单独发：并入 handEnded.result.awards，避免与结算重复（§5.6）
          break
        case 'handComplete': {
          const redacted = redactHandState(stateAfter, perspective)
          if (redacted.result !== null) out.push({ type: 'handEnded', handId, result: redacted.result })
          break
        }
        default:
          break
      }
    }
    flushPosts()
    return out
  }

  /** 手牌结算后的房间级收尾：筹码回写、按钮指针推进、是否还打得下去。 */
  function settleHand(final: HandState): void {
    if (final.result === null) return
    players = applyFinalStacks(players, final.result.finalStacks)
    // 红线 #25：只有 complete 的手牌才推进按钮指针
    prevBigBlindSeat = final.bigBlindSeat
    prevSmallBlindSeat = final.smallBlindPosition ?? final.smallBlindSeat
    pendingLegal = null
    if (!canDealNextHand(players)) phase = 'ended'
    emitRoomSnapshot()
  }

  function startNextHand(): void {
    if (closed) return
    if (state !== null && state.result === null) {
      emitError('E_HAND_IN_PROGRESS', '上一手还没打完')
      return
    }
    if (players.length < SEAT_LIMITS.MIN) {
      emitError('E_NOT_ENOUGH_PLAYERS', `至少需要 ${SEAT_LIMITS.MIN} 人`)
      return
    }
    if (!canDealNextHand(players)) {
      phase = 'ended'
      emitRoomSnapshot()
      emitError('E_NOT_ENOUGH_PLAYERS', '只剩一人还有筹码，本局结束')
      return
    }

    const nextNo = handNo + 1
    const res = startHand({
      config: {
        handId: `hnd_${ulid(now())}` as HandId,
        maxSeats: roomConfig.maxSeats,
        smallBlind: roomConfig.smallBlind,
        bigBlind: roomConfig.bigBlind,
        anteMode: roomConfig.anteMode,
        ante: roomConfig.ante,
        burnCards: true,
        showdownPolicy: roomConfig.showdownPolicy,
        newPlayerPolicy: roomConfig.newPlayerPolicy,
        missedBlindPolicy: roomConfig.missedBlindPolicy,
        assist: roomConfig.assist,
        deckVariant: roomConfig.deckVariant,
      },
      seats: toSeatSnapshots(players),
      prevBigBlindSeat,
      prevSmallBlindSeat,
      // ★ 红线 #15：CSPRNG 洗牌，引擎自己一次 RNG 都不调
      deck: shuffleDeck(rng, roomConfig.deckVariant),
      handNo: nextNo,
    })
    if (!res.ok) {
      emitError(res.error.code, res.error.message)
      return
    }
    handNo = nextNo
    phase = 'running'
    const fresh = res.value.state
    state = fresh
    // 新的一手：先把旧视角清掉，别让上一手的 hero 在发牌动画里就亮着
    perspective = null
    pendingLegal = null
    emitRoomSnapshot()
    // stateBefore 传 null：这是本手的第一批事件，board 从 0 张、没有任何人亮过牌
    enqueueBatch(res.value.events, null, fresh)
  }

  function handlePlayerAction(msg: Extract<ClientMessage, { type: 'playerAction' }>): void {
    const s = state
    if (s === null || s.result !== null) {
      emitError('E_HAND_NOT_IN_BETTING', '牌局还没开始', msg.clientActionId)
      return
    }
    if (msg.handId !== s.config.handId) {
      emitError('E_WRONG_HAND', '已经换了一手牌', msg.clientActionId)
      return
    }
    if (msg.actionNo !== undefined && msg.actionNo !== s.actionLog.length) {
      emitError('E_STALE_ACTION', '牌局已经往前走了，这一步没生效', msg.clientActionId)
      return
    }
    const seat = s.actionOn
    if (seat === null) {
      emitError('E_HAND_NOT_IN_BETTING', '现在没有人要行动', msg.clientActionId)
      return
    }
    const intent = toIntent(msg.action, msg.to)
    if (intent === null) {
      emitError('E_MALFORMED', `无法解析的动作: ${msg.action}`, msg.clientActionId)
      return
    }
    // ★ seat 取自引擎状态，不取自消息。本地模式的「连接身份」= 当前持有行动权的人，
    //   也就是手里拿着这部手机的那个人（红线 #37 的本地对应物）。
    const res = applyAction(s, {
      handId: s.config.handId as HandId,
      seat,
      intent,
      seq: s.actionLog.length,
      source: 'human',
    })
    if (!res.ok) {
      emitError(res.error.code, res.error.message, msg.clientActionId)
      // 行动被拒后重发一份权威视图，避免 UI 停在 pending 态
      emitHandSnapshot()
      return
    }
    const before = s
    state = res.value.state
    enqueueBatch(res.value.events, before, state)
  }

  function send(msg: ClientMessage): void {
    if (closed) return
    switch (msg.type) {
      case 'startGame':
        if (phase === 'running' && state !== null && state.result === null) return
        startNextHand()
        break
      case 'nextHand':
        if (nextHandTimer !== null) {
          sched.cancel(nextHandTimer)
          nextHandTimer = null
        }
        startNextHand()
        break
      case 'playerAction':
        handlePlayerAction(msg)
        break
      case 'requestSnapshot':
        emitRoomSnapshot()
        emitHandSnapshot()
        break
      case 'setPerspective':
        // ⚠️ 这是回放/调试通道。**绝不能**在传手机模式的 UI 里放一个「切换视角」按钮：
        // 几个人共用一部手机时，那等于给所有人开了一个看别人底牌的开关。
        // 正常流程里视角只由 actionOn 驱动（reason:'turn'）。
        setPerspective(msg.seat, 'manual')
        emitHandSnapshot()
        break
      case 'leaveRoom':
        close()
        break
      default:
        break
    }
  }

  function close(): void {
    if (closed) return
    closed = true
    if (timer !== null) sched.cancel(timer)
    if (nextHandTimer !== null) sched.cancel(nextHandTimer)
    timer = null
    nextHandTimer = null
    queue.length = 0
    listeners.clear()
    // 私密状态立刻丢弃，别让底牌留在闭包里等 GC
    state = null
    perspective = null
    pendingLegal = null
  }

  return {
    mode: 'local',
    perspectiveSeat: () => perspective,
    connect(): Promise<void> {
      emit({ type: 'welcome', mode: 'local', protocolVersion: PROTOCOL_VERSION, serverTime: now() })
      emitRoomSnapshot()
      return Promise.resolve()
    },
    send,
    onMessage(cb) {
      listeners.add(cb)
      return () => {
        listeners.delete(cb)
      }
    },
    close,
  }
}
